import React, { Component, type ReactNode, type ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import { reportError } from './error-reporter';

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode | ((props: { error: Error | null; resetError: () => void }) => ReactNode);
  onReset?: () => void;
  className?: string;
  title?: string;
  message?: string;
  reloadLabel?: string;
  resetLabel?: string;
  showDetailsLabel?: string;
  hideDetailsLabel?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
  showDetails: boolean;
}

/**
 * Generic ErrorBoundary component for React applications.
 *
 * Catches unhandled errors in child component trees during rendering or lifecycle,
 * forwards them to the backend via `reportError`, and displays a styled dark-mode
 * fallback UI with recovery actions.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public override state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    componentStack: null,
    showDetails: false,
  };

  public static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ componentStack: errorInfo.componentStack ?? null });
    reportError(error, errorInfo.componentStack ?? undefined);
  }

  public resetError = (): void => {
    this.props.onReset?.();
    this.setState({
      hasError: false,
      error: null,
      componentStack: null,
      showDetails: false,
    });
  };

  private toggleDetails = (): void => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  public override render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (typeof this.props.fallback === 'function') {
      return this.props.fallback({
        error: this.state.error,
        resetError: this.resetError,
      });
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    const {
      title = 'Something went wrong',
      message = 'An unexpected error occurred and has been reported to the server.',
      reloadLabel = 'Reload Page',
      resetLabel = 'Try Again',
      showDetailsLabel = 'Show error details',
      hideDetailsLabel = 'Hide error details',
      className = '',
    } = this.props;

    const { error, componentStack, showDetails } = this.state;

    return (
      <div
        role="alert"
        aria-live="assertive"
        className={`flex flex-col items-center justify-center p-6 my-6 mx-auto w-full max-w-xl rounded-xl bg-[#151325] border border-red-500/20 text-white shadow-2xl ${className}`}
      >
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 mb-4">
          <AlertTriangle className="w-6 h-6" />
        </div>

        <h2 className="text-lg font-semibold text-white text-center mb-1">{title}</h2>
        <p className="text-sm text-zinc-400 text-center mb-6 max-w-md">{message}</p>

        <div className="flex flex-wrap items-center justify-center gap-3 mb-4">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-red-600 hover:bg-red-500 active:bg-red-700 text-white transition-colors cursor-pointer"
          >
            <RefreshCw className="w-4 h-4" />
            {reloadLabel}
          </button>
          <button
            type="button"
            onClick={this.resetError}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-zinc-800 hover:bg-zinc-700 active:bg-zinc-900 border border-zinc-700 text-zinc-200 transition-colors cursor-pointer"
          >
            <RotateCcw className="w-4 h-4" />
            {resetLabel}
          </button>
        </div>

        {(error || componentStack) && (
          <div className="w-full mt-2 pt-4 border-t border-zinc-800 text-left">
            <button
              type="button"
              onClick={this.toggleDetails}
              className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-200 transition-colors cursor-pointer mb-2"
            >
              {showDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {showDetails ? hideDetailsLabel : showDetailsLabel}
            </button>

            {showDetails && (
              <div className="space-y-2 mt-2">
                {error && (
                  <div className="p-3 bg-black/40 border border-zinc-800/80 rounded-lg text-xs font-mono text-red-300 break-words whitespace-pre-wrap max-h-48 overflow-y-auto">
                    <div className="font-semibold text-red-400 mb-1">{error.name}: {error.message}</div>
                    {error.stack && <div>{error.stack}</div>}
                  </div>
                )}
                {componentStack && (
                  <div className="p-3 bg-black/40 border border-zinc-800/80 rounded-lg text-xs font-mono text-zinc-400 break-words whitespace-pre-wrap max-h-48 overflow-y-auto">
                    <div className="font-semibold text-zinc-300 mb-1">Component Stack:</div>
                    <div>{componentStack}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}
